/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {Session} from '../../../components/session/session';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../../common/directives/list-default-actions.component';
import {stateTitleView} from '../../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../../routing/state-utils';
import {RoleApiService, RoleModel} from './role-api.service';
import {RoleCopyModalComponent} from './role-copy-modal.component';
import {RoleItemPageComponent} from './role-item-page.component';
import {RoleRoutePageComponent} from './role-route-page.component';

export function roleStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(RoleApiService);
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const rh = adnRouterHelper.createRouterHelper('roles.role', RoleApiService, 'app.adm', 'app/admin');
  const copyConfig = {
    modalComponent: RoleCopyModalComponent,
    stateUrl: 'app.adm.roles.role',
    lcase: 'role',
    ucase: 'Role'
  };

  states.add(rh.listPath(), rh.list({
    data: {app: ['ADVERTISING', 'DATA', 'POLITICAL']},
    resolve: {
      roleActionsReadOnly: function(listReadOnly: boolean) {
        return listReadOnly || !Session.hasStrongAuthentication();
      }
    },
    views: {
      'content@app': {component: RoleRoutePageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'roleActionsReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    data: {app: ['ADVERTISING', 'DATA', 'POLITICAL']},
    resolve: {
      rolePage: function($stateParams: any, readOnly: boolean) {
        const allowedCopyConfig = Session.hasStrongAuthentication() ? copyConfig : undefined;
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState<RoleModel>(
            {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []}, true, !!readOnly, api, 'Role', 'name', allowedCopyConfig
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, !!readOnly, api, 'Role', 'name', allowedCopyConfig));
      },
      model: function(rolePage: SignalAdminResourcePageState<RoleModel>) {
        return rolePage.model();
      }
    },
    views: {
      'content@app': {component: RoleItemPageComponent, bindings: {state: 'rolePage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'rolePage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'rolePage'}}
    }
  }));
  return states.build();
}
